import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { type Camera, type Group, Vector3 } from 'three';

import { usePreloadStore } from 'state/preloadStore';
import { publicAssets } from 'utils/gcs';

const PAPER_FILENAMES = ['paper_1.glb', 'paper_2.glb', 'paper_3.glb'];
const PLASTIC_BAG_FILENAMES = ['plastic_bag_model.glb'];
const STICK_FILENAMES = [
  'stick_1.glb',
  'stick_2.glb',
  'stick_3.glb',
  'stick_4.glb',
];

export const FLYING_DEBRIS_URLS = [
  ...PAPER_FILENAMES,
  ...PLASTIC_BAG_FILENAMES,
  ...STICK_FILENAMES,
].map(filename => publicAssets(`/models/flying-debris/${filename}`));

const randomStartPosition = (camera: Camera): Vector3 => {
  const fromTop = Math.random() >= 0.5;
  const fromRight = !fromTop;
  const corner = new Vector3(
    fromRight ? 2 : 2 - 1.9 * Math.random(),
    fromTop ? 2 : 2 - 1.9 * Math.random(),
    0
  ).unproject(camera);
  const dir = new Vector3().subVectors(corner, camera.position).normalize();
  const distance = 2 + 5 * Math.random();
  return corner.add(dir.multiplyScalar(distance));
};

const randomFinalPosition = (camera: Camera): Vector3 => {
  const toBottom = Math.random() >= 0.5;
  const toLeft = !toBottom;
  const corner = new Vector3(
    toLeft ? -2 : -2 + 1.9 * Math.random(),
    toBottom ? -2 : -2 + 1.9 * Math.random(),
    0
  ).unproject(camera);
  const dir = new Vector3().subVectors(corner, camera.position).normalize();
  const distance = 1 + 2 * Math.random();
  return corner.add(dir.multiplyScalar(distance));
};

export const FlyingDebris = () => {
  const groupRef = useRef<Group | null>(null);
  const camera = useThree(state => state.camera);

  const objsToSpawn = useRef(8);

  const glTFs = usePreloadStore(s => s.glTFs);

  useEffect(() => {
    const timeoutIds: number[] = [];

    const spawn = () => {
      const timeoutId = setTimeout(() => {
        const idx = timeoutIds.indexOf(timeoutId);
        if (idx >= 0) timeoutIds.splice(idx, 1);
        spawn();
      }, Math.random() * 800) as unknown as number;

      if (!groupRef.current) return;

      if (objsToSpawn.current === 0) return;
      objsToSpawn.current--;

      const startPosition = randomStartPosition(camera);
      const finalPosition = randomFinalPosition(camera);

      const url =
        FLYING_DEBRIS_URLS[
          Math.floor(Math.random() * FLYING_DEBRIS_URLS.length)
        ];
      const debris = glTFs[url].scene.clone();

      const pathname = new URL(url, 'https://example.com').pathname;
      const filename = pathname.substring(pathname.lastIndexOf('/') + 1);

      if (PAPER_FILENAMES.includes(filename)) {
        debris.scale.setScalar(0.3 + 1 * Math.random());
      } else if (STICK_FILENAMES.includes(filename)) {
        debris.scale.setScalar(0.5 + 4 * Math.random());
      }

      debris.position.copy(startPosition);
      debris.userData = {
        startPosition,
        finalPosition,
        progress: 0,
        speed: 0.4 + Math.random() * 1.5,
        angularSpeed: new Vector3(
          Math.random() * 2,
          Math.random() * 2,
          Math.random() * 2
        ),
      };

      groupRef.current.add(debris);
    };

    spawn();

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [camera, glTFs]);

  useFrame((_, deltaTime) => {
    if (!groupRef.current) return;

    for (let i = groupRef.current.children.length - 1; i >= 0; i--) {
      const debris = groupRef.current.children[i];
      debris.userData.progress += debris.userData.speed * deltaTime;
      if (debris.userData.progress >= 1) {
        groupRef.current.remove(debris);
        objsToSpawn.current++;
        continue;
      }
      debris.position.lerpVectors(
        debris.userData.startPosition,
        debris.userData.finalPosition,
        debris.userData.progress
      );
      const angularSpeed = debris.userData.angularSpeed as Vector3;
      debris.rotation.x += angularSpeed.x * deltaTime;
      debris.rotation.y += angularSpeed.y * deltaTime;
      debris.rotation.z += angularSpeed.z * deltaTime;
    }
  });

  return <group ref={groupRef}></group>;
};
